import React, {useState, useEffect} from 'react';
import { fetchOverviewMetrics, IOverviewMetrics } from '../../api';
import { useWebsite } from '../../hooks/useWebsite';
import { useDateRange } from '../../hooks/useDateRange';

interface CardProps {
  label: string;
  value: number;
  isLoading: boolean;
  format?: (value: number) => string
}

const Card: React.FC<CardProps> = ({label, value, format, isLoading}) => {
  return (
    <div className="card card-flush flex-fill">
      <div className="card-header d-flex flex-column align-items-center justify-content-center py-3">
        <span className="text-gray-500 fw-semibold fs-5">{label}</span>
        <div className="d-flex justify-content-center align-items-center mt-2">
          {isLoading ? (
            <div className="spinner-border spinner-border-lg text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : (
            <span className="fs-1 fw-bold text-gray-900">
              {format ? format(value) : value}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

const formatShortNumber = (num: number): string => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return num.toString();
};

const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  const minStr = mins > 0 ? `${mins} min` : '';
  const secStr = secs > 0 || mins === 0 ? `${secs} sec` : '';
  return [minStr, secStr].filter(Boolean).join(' ');
};

const formatPrc = (value: number, decimals = 2): string => {
  return `${(value * 100).toFixed(decimals)}%`;
};

export const Indicators = () => {
  const { websiteId } = useWebsite();
  const { from, to, timezone } = useDateRange();
  const initialValue: IOverviewMetrics = {
    visitors: 0,
    visits: 0,
    pageViews: 0,
    duration: 0,
    bounceRate: 0,
    events: 0
  }

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<IOverviewMetrics>(initialValue);

  const load = async () => {
    try {
      setIsLoading(true)
      await fetchOverviewMetrics(websiteId, { from, to, timezone} ).then((data) => { void(data && setData(data)) });
    } catch (e: unknown) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [from, to]);

  return (
    <div className="row justify-content-center">
      <div className="col-md-2 col-6 mb-3 d-flex justify-content-center">
        <Card label="Visitors" value={data.visitors} isLoading={isLoading} format={formatShortNumber} />
      </div>
      <div className="col-md-2 col-6 mb-3 d-flex justify-content-center">
        <Card label="Visits" value={data.visits} isLoading={isLoading} format={formatShortNumber} />
      </div>
      <div className="col-md-2 col-6 mb-3 d-flex justify-content-center">
        <Card label="Page View" value={data.pageViews} isLoading={isLoading} format={formatShortNumber} />
      </div>
      <div className="col-md-2 col-6 mb-3 d-flex justify-content-center">
        <Card label="Duration" value={data.duration} isLoading={isLoading} format={formatDuration} />
      </div>
      <div className="col-md-2 col-6 mb-3 d-flex justify-content-center">
        <Card label="Bounce rate" value={data.bounceRate} isLoading={isLoading} format={formatPrc} />
      </div>

      <div className="col-md-2 col-6 mb-3 d-flex justify-content-center">
        <Card label="Events" value={data.events} isLoading={isLoading} format={formatShortNumber} />
      </div>
    </div>
  )
}
